import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, Linkedin, Twitter, Share2 } from 'lucide-react';
import { blogPostsData, profileData } from '../data/mock';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = blogPostsData.find(p => p.id === slug);

  if (!post) {
    return (
      <div className="min-h-screen bg-[#faf6ee]">
        <Header />
        <div className="pt-32 pb-16 text-center">
          <h1 className="text-2xl font-bold text-[#152846] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Post Not Found</h1>
          <p className="text-[#5c6878] mb-6">The blog post you're looking for doesn't exist.</p>
          <Link to="/blog" className="inline-flex items-center gap-2 px-6 py-3 bg-gold-400 text-black font-semibold rounded-full hover:bg-gold-300 transition-colors" data-testid="back-to-blog-btn">
            <ArrowLeft size={18} strokeWidth={2.5} />Back to Blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedPosts = blogPostsData.filter(p => p.id !== post.id).slice(0, 2);
  const shareUrl = window.location.href;

  return (
    <div className="min-h-screen bg-[#faf6ee]" data-testid="blog-post-page">
      <Header />

      <section className="pt-24 pb-8">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <button onClick={() => navigate('/blog')} className="inline-flex items-center gap-2 text-[#5c6878] hover:text-gold-400 mb-8 transition-colors" data-testid="back-to-blog-nav">
            <ArrowLeft size={18} strokeWidth={2.5} />Back to Blog
          </button>
          <span className="inline-block px-3 py-1 bg-gold-400/10 text-gold-400 text-sm font-semibold rounded-full mb-4">{post.category}</span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#152846] mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>{post.title}</h1>
          <div className="flex flex-wrap items-center gap-6 text-[#8b94a3] mb-8">
            <div className="flex items-center gap-3">
              <img src={profileData.photo} alt={post.author} className="w-12 h-12 rounded-full object-cover border-2 border-gold-400/30" />
              <div>
                <p className="font-semibold text-[#152846]">{post.author}</p>
                <p className="text-sm text-[#8b94a3]">{profileData.title}</p>
              </div>
            </div>
            <span className="flex items-center gap-1"><Calendar size={16} strokeWidth={2.5} />{new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
            <span className="flex items-center gap-1"><Clock size={16} strokeWidth={2.5} />{post.readTime}</span>
          </div>
        </div>
      </section>

      <section className="pb-12">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="rounded-2xl overflow-hidden shadow-lg border border-[#e6dfd0]">
            <img src={post.image} alt={post.title} className="w-full h-64 sm:h-96 object-cover" />
          </div>
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="prose prose-lg prose-invert max-w-none">
            {post.content.split('\n\n').map((paragraph, index) => {
              if (paragraph.startsWith('## ')) {
                return <h2 key={index} className="text-2xl font-bold text-[#152846] mt-10 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>{paragraph.replace('## ', '')}</h2>;
              }
              if (paragraph.startsWith('- ')) {
                const items = paragraph.split('\n').filter(item => item.startsWith('- '));
                return (
                  <ul key={index} className="list-disc list-inside space-y-2 text-[#5c6878] my-4">
                    {items.map((item, i) => <li key={i}>{item.replace('- ', '')}</li>)}
                  </ul>
                );
              }
              return <p key={index} className="text-[#5c6878] leading-relaxed mb-6">{paragraph}</p>;
            })}
          </div>

          {post.linkedinUrl && (
            <div className="mt-10">
              <a
                href={post.linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 bg-gold-400 text-black font-semibold rounded-full hover:bg-gold-300 transition-colors"
                data-testid="read-on-linkedin-btn"
              >
                <Linkedin size={18} strokeWidth={2.5} />Read the original on LinkedIn
              </a>
            </div>
          )}

          <div className="mt-12 pt-8 border-t border-[#e6dfd0]">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <p className="font-semibold text-[#152846]">Share this article</p>
              <div className="flex items-center gap-3">
                <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-[#fffdf8] border border-[#e6dfd0] rounded-full flex items-center justify-center text-[#8b94a3] hover:bg-gold-400 hover:text-black hover:border-gold-400 transition-all" aria-label="Share on LinkedIn" data-testid="share-linkedin"><Linkedin size={18} strokeWidth={2.5} /></a>
                <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-[#fffdf8] border border-[#e6dfd0] rounded-full flex items-center justify-center text-[#8b94a3] hover:bg-gold-400 hover:text-black hover:border-gold-400 transition-all" aria-label="Share on Twitter" data-testid="share-twitter"><Twitter size={18} strokeWidth={2.5} /></a>
                <button onClick={() => navigator.clipboard.writeText(shareUrl)} className="w-10 h-10 bg-[#fffdf8] border border-[#e6dfd0] rounded-full flex items-center justify-center text-[#8b94a3] hover:bg-gold-400 hover:text-black hover:border-gold-400 transition-all" aria-label="Copy link" data-testid="share-copy"><Share2 size={18} strokeWidth={2.5} /></button>
              </div>
            </div>
          </div>

          <div className="mt-12 p-6 bg-[#fffdf8] rounded-2xl border border-[#e6dfd0]">
            <div className="flex items-start gap-4">
              <img src={profileData.photo} alt={post.author} className="w-16 h-16 rounded-full object-cover border-2 border-gold-400/30" />
              <div>
                <h3 className="font-bold text-[#152846] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>About {post.author}</h3>
                <p className="text-gold-400 font-medium text-sm mb-2">{profileData.title} at {profileData.company}</p>
                <p className="text-[#5c6878] text-sm">{profileData.tagline}</p>
                <a href={profileData.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-gold-400 text-sm font-medium mt-3 hover:underline"><Linkedin size={14} strokeWidth={2.5} />Connect on LinkedIn</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {relatedPosts.length > 0 && (
        <section className="py-16 bg-[#f3ede0]">
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-[#152846] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>Related Articles</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {relatedPosts.map((relatedPost) => (
                <Link key={relatedPost.id} to={`/blog/${relatedPost.id}`} className="group bg-[#fffdf8] rounded-xl overflow-hidden border border-[#e6dfd0] hover:border-gold-400/30 hover:shadow-lg transition-all duration-300" data-testid={`related-post-${relatedPost.id}`}>
                  <div className="h-40 overflow-hidden"><img src={relatedPost.image} alt={relatedPost.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                  <div className="p-5">
                    <span className="text-xs font-semibold text-gold-400">{relatedPost.category}</span>
                    <h3 className="font-bold text-[#152846] mt-2 group-hover:text-gold-400 transition-colors line-clamp-2" style={{ fontFamily: "'Playfair Display', serif" }}>{relatedPost.title}</h3>
                    <p className="text-sm text-[#8b94a3] mt-2">{relatedPost.readTime}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default BlogPost;
